import React, { useState, useEffect } from 'react';
import { Loader2, TrendingUp, Briefcase, MapPin, Tag, Users, Trophy, Zap, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VERTICAL_LABELS = {
  accounting_finance: 'Accounting & Finance',
  healthcare: 'Healthcare',
  logistics_supply_chain: 'Logistics & Supply Chain',
  hr_compliance: 'HR & Compliance',
};

const PatternCard = ({ rank, title, subtitle, avgScore, totalCandidates, highFit, reason, exampleRoles, testId }) => (
  <div className="flex items-start gap-3 p-2.5 bg-white border border-gray-200 rounded-lg hover:border-[#D4AF37]/30 transition-colors" data-testid={testId}>
    <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${rank === 0 ? 'bg-[#D4AF37]/15 text-[#8B7425]' : 'bg-gray-100 text-gray-500'}`}>
      <span className="text-[9px] font-bold">#{rank + 1}</span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold text-[#0A1F44] truncate">{title}</span>
        {rank === 0 && <Trophy className="h-3 w-3 text-[#D4AF37] flex-shrink-0" />}
      </div>
      {subtitle && <p className="text-[9px] text-gray-400">{subtitle}</p>}
      <div className="flex items-center gap-3 mt-1">
        <span className={`text-[10px] font-bold ${avgScore >= 50 ? 'text-emerald-600' : avgScore >= 30 ? 'text-amber-600' : 'text-gray-500'}`}>
          {avgScore} avg
        </span>
        <span className="text-[9px] text-gray-400">{totalCandidates} candidates</span>
        {highFit > 0 && <span className="text-[9px] text-emerald-600 font-medium">{highFit} strong-fit</span>}
      </div>
      {reason && <p className="text-[8px] text-gray-500 mt-0.5">{reason}</p>}
      {exampleRoles && exampleRoles.length > 0 && (
        <p className="text-[8px] text-gray-400 mt-0.5 truncate">
          Roles: {exampleRoles.slice(0, 2).join(', ')}
        </p>
      )}
    </div>
    <div className="flex-shrink-0">
      <div className="h-1.5 w-16 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${avgScore >= 50 ? 'bg-emerald-500' : avgScore >= 30 ? 'bg-amber-400' : 'bg-gray-300'}`}
          style={{ width: `${Math.min(avgScore, 100)}%` }} />
      </div>
    </div>
  </div>
);

const PresetCard = ({ preset, onApply, testId }) => (
  <div className="p-3 bg-gradient-to-r from-[#D4AF37]/5 to-white border border-[#D4AF37]/20 rounded-lg" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Zap className="h-3 w-3 text-[#D4AF37] flex-shrink-0" />
          <span className="text-[10px] font-bold text-[#0A1F44] truncate">{preset.label}</span>
        </div>
        <p className="text-[8px] text-gray-500 mt-0.5">{preset.reason}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {preset.search_titles?.map((t, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-[#0A1F44]/5 text-[#0A1F44] text-[7px] font-medium rounded">{t}</span>
          ))}
          {preset.seniority && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[7px] font-medium rounded">{preset.seniority}</span>}
          {preset.title_keywords?.slice(0, 3).map((k, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[7px] rounded">{k}</span>
          ))}
        </div>
      </div>
      <button onClick={() => onApply(preset)}
        className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0A1F44] text-white text-[9px] font-bold rounded-md hover:bg-[#0d2a5c] transition-colors flex-shrink-0 ml-3"
        data-testid={`${testId}-apply-btn`}>
        Apply <ArrowRight className="h-2.5 w-2.5" />
      </button>
    </div>
  </div>
);

export const CrossRolePatterns = ({ onApplyPattern }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('titles');

  useEffect(() => { loadPatterns(); }, []);

  const loadPatterns = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/cross-role-patterns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!d.error) setData(d);
    } catch { /* silent */ }
    setLoading(false);
  };

  const handleApplyPreset = (preset) => {
    if (onApplyPattern) {
      onApplyPattern({
        title: preset.search_titles?.[0] || '',
        keywords: (preset.title_keywords || []).join(', '),
        seniority: preset.seniority || '',
        vertical: preset.vertical || '',
      });
      toast.success(`Pattern applied to simulator: "${preset.label}"`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="cross-role-patterns-loading">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data || data.total_candidates_analyzed === 0) {
    return (
      <div className="text-center py-6" data-testid="cross-role-patterns-empty">
        <p className="text-xs text-gray-400">No sourcing data yet. Source candidates across roles to see patterns.</p>
      </div>
    );
  }

  const sections = [
    { key: 'titles', icon: Briefcase, label: 'Top Titles', items: data.top_titles, nameKey: 'title' },
    { key: 'seniority', icon: TrendingUp, label: 'Seniority Levels', items: data.top_seniority, nameKey: 'seniority' },
    { key: 'keywords', icon: Tag, label: 'Keywords', items: data.top_keywords, nameKey: 'keyword' },
  ];

  return (
    <div className="space-y-4" data-testid="cross-role-patterns">
      {/* Header stats */}
      <div className="flex items-center gap-4 text-[10px] text-gray-500">
        <span><strong className="text-[#0A1F44]">{data.total_roles_analyzed}</strong> roles analyzed</span>
        <span><strong className="text-[#0A1F44]">{data.total_candidates_analyzed}</strong> candidates analyzed</span>
      </div>

      {/* Quick-Apply Presets */}
      {data.presets && data.presets.length > 0 && (
        <div data-testid="patterns-presets">
          <p className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-wider mb-2">Apply Pattern to Simulator</p>
          <div className="space-y-2">
            {data.presets.map((p, i) => (
              <PresetCard key={i} preset={p} onApply={handleApplyPreset} testId={`preset-${i}`} />
            ))}
          </div>
        </div>
      )}

      {/* Pattern Sections */}
      {sections.map(sec => {
        const Icon = sec.icon;
        const isOpen = expandedSection === sec.key;
        const items = sec.items || [];
        if (items.length === 0) return null;
        return (
          <div key={sec.key} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`patterns-section-${sec.key}`}>
            <button
              onClick={() => setExpandedSection(isOpen ? null : sec.key)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon className="h-3.5 w-3.5 text-[#D4AF37]" />
                <span className="text-[11px] font-bold text-[#0A1F44]">{sec.label}</span>
                <span className="text-[8px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{items.length}</span>
              </div>
              {isOpen ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 space-y-1.5 border-t border-gray-100 pt-2">
                {items.map((item, i) => (
                  <PatternCard
                    key={i}
                    rank={i}
                    title={item[sec.nameKey]}
                    subtitle={item.verticals?.map(v => VERTICAL_LABELS[v] || v).join(', ')}
                    avgScore={item.weighted_avg}
                    totalCandidates={item.total_candidates}
                    highFit={item.total_high_fit}
                    reason={`${item.data_points} data point${item.data_points > 1 ? 's' : ''}, confidence: ${item.confidence_score}`}
                    exampleRoles={item.example_roles}
                    testId={`pattern-${sec.key}-${i}`}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Vertical Summary */}
      {data.vertical_summary && data.vertical_summary.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="patterns-section-verticals">
          <button
            onClick={() => setExpandedSection(expandedSection === 'verticals' ? null : 'verticals')}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-[#D4AF37]" />
              <span className="text-[11px] font-bold text-[#0A1F44]">Vertical Performance</span>
            </div>
            {expandedSection === 'verticals' ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
          </button>
          {expandedSection === 'verticals' && (
            <div className="px-3 pb-3 space-y-1.5 border-t border-gray-100 pt-2">
              {data.vertical_summary.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg" data-testid={`pattern-vertical-${i}`}>
                  <div>
                    <span className="text-[10px] font-bold text-[#0A1F44]">{v.label}</span>
                    <p className="text-[8px] text-gray-400">{v.roles_count} role{v.roles_count > 1 ? 's' : ''}, {v.total_candidates} candidates</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-[11px] font-bold ${v.avg_score >= 50 ? 'text-emerald-600' : v.avg_score >= 30 ? 'text-amber-600' : 'text-gray-500'}`}>{v.avg_score}</span>
                    <p className="text-[7px] text-gray-400">avg score</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
